import i18n from 'i18next';
import * as Sentry from '@sentry/react';
import { toast } from '@hooks/useToast';

const DEDUPE_MS = 5000;
export const FIREWALL_CHALLENGE_STATUS = 202;
const lastShown: Record<string, number> = {};

export const getErrorStatus = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('status' in error)) return undefined;
  return typeof error.status === 'number' ? error.status : undefined;
};

const isBrowserAbort = (error: unknown) => error instanceof Error && error.name === 'AbortError';

export const isNetworkError = (error: unknown) =>
  error instanceof TypeError ||
  (error instanceof DOMException && error.name === 'TimeoutError') ||
  isBrowserAbort(error);

export const isFirewallChallenge = (error: unknown) =>
  getErrorStatus(error) === FIREWALL_CHALLENGE_STATUS;

const getMessageKey = (error: unknown) => {
  if (isNetworkError(error)) return 'messages.failure.request.network';
  const status = getErrorStatus(error);
  if (status === FIREWALL_CHALLENGE_STATUS) return 'messages.failure.request.blocked';
  if (status === 403) return 'messages.failure.request.forbidden';
  if (status && status >= 500) return 'messages.failure.request.server';
  return undefined;
};

const getErrorUrl = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('url' in error)) return undefined;
  return typeof error.url === 'string' ? error.url : undefined;
};

const NETWORK_ERROR_RETRIES = 2;
const REDIRECT_STATUSES = [403, 404, 410];

export const retryNetworkError = (failureCount: number, error: unknown) =>
  isNetworkError(error) && failureCount < NETWORK_ERROR_RETRIES;

export const isRedirectError = (error: unknown) =>
  REDIRECT_STATUSES.includes(getErrorStatus(error) ?? 0);

export const isSilentError = (error: unknown) =>
  getErrorStatus(error) === 403 && Boolean(getErrorUrl(error)?.startsWith('dropdowns/'));

export const isNotifiedGlobally = (error: unknown) => Boolean(getMessageKey(error));

const FETCH_FAILURE_MESSAGES = ['Failed to fetch', 'NetworkError', 'Load failed', 'network error'];

const isConnectivityError = (error: unknown) =>
  (error instanceof DOMException && error.name === 'TimeoutError') ||
  isBrowserAbort(error) ||
  (error instanceof TypeError &&
    FETCH_FAILURE_MESSAGES.some((message) => error.message.includes(message)));

const isExpectedError = (error: unknown) => {
  const status = getErrorStatus(error);
  if (status !== undefined) return status < 500;
  return isConnectivityError(error);
};

const normalizeUrl = (url?: string) =>
  url
    ?.split('?')[0]
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+/gi, ':id');

export const reportUnexpectedError = (
  error: unknown,
  source: 'query' | 'mutation',
  key?: readonly unknown[],
) => {
  if (isExpectedError(error)) return;
  const url = normalizeUrl(getErrorUrl(error));
  Sentry.captureException(error, {
    tags: { source, status: getErrorStatus(error), url },
    extra: { key },
  });
};

export const notifyQueryError = (error: unknown, messageKey?: string) => {
  const key = messageKey || getMessageKey(error) || 'messages.failure.request.default';
  const now = Date.now();
  if (now - (lastShown[key] || 0) < DEDUPE_MS) return;
  // eslint-disable-next-line immutable/no-mutation
  lastShown[key] = now;
  toast({ title: i18n.t(key, { ns: 'app' }), variant: 'destructive' });
};
